import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ColorMaterialView } from "@montana/pages/color-material-page/color-material-view.component";
import { error } from "@montana/services/logger";
import { IContent } from "@montana/models/generated/cms.interface";
import { Modal } from ".";
import { useConnectedServiceFunctions } from "../use-connected-service-functions";
import { fetchPageData as fetchPageDataConfig } from "@montana/services/content";
import { StandardView } from "@montana/pages/standard-view.component";

const CONTENT_RENDERERS: { [key: string]: any } = {
    ColorMaterialDescriptionPage: ColorMaterialView,
    ContentPage: StandardView,
};

export interface IDynamicContentModalProps {
    className?: string;
    contentUrl: string;
    onModalClosed?: () => void;
}

export interface IDynamicContentModalRef {
    open: () => void;
}

export const DynamicContentModal = forwardRef<IDynamicContentModalRef, IDynamicContentModalProps>((props, ref) => {
    const [fetchPageData] = useConnectedServiceFunctions(fetchPageDataConfig);
    const [content, setContent] = useState<IContent>();
    const modalRef = useRef<Modal>(null);

    // If the URL changes, reset the content.
    useEffect(() => setContent(undefined), [props.contentUrl]);

    const fetchContent = useCallback(() => fetchPageData(props.contentUrl)
        .then(r => r.ok && r.json())
        .then(setContent)
        .catch(() => error(`[DynamicContentModal] Unable to fetch content from url: ${props.contentUrl}`)),
    [fetchPageData, props.contentUrl]);

    useImperativeHandle(ref, () => ({
        open: () => {
            !content && fetchContent();
            modalRef.current?.open();
        }
    }), []);

    const Renderer = content && CONTENT_RENDERERS[content.kind];
    if (content && !Renderer) error(`[DynamicContentModal] Missing renderer for content kind "${content.kind}"`);

    return (
        <Modal
            className={props.className}
            position="center"
            ref={modalRef}
            onModalClosed={props.onModalClosed}
        >
            {Renderer && <Renderer {...content} />}
        </Modal>
    );
});
